import { generatePath } from 'react-router'

export const PATH_STRINGS = {
  project: '/project/:projectId',
  floor: '/project/:projectId/floor/:floorId',
  point: '/project/:projectId/floor/:floorId/point/:pointId',
  full: '/project/:projectId/floor/:floorId/point/:pointId/full',
  safetyOverview: '/project/:projectId/safety', //use ?category=<category>
  safetyMetricDetail: '/project/:projectId/safety/detail', //use ?category=<category>
  safetyMetricDetailFloor: '/project/:projectId/safety/floor/:floorId/detail', //use ?category=<category>
  safetyMetricDetailFloorPoint:
    '/project/:projectId/safety/floor/:floorId/point/:pointId/detail', //use ?category=<category>
  safetyGallery: '/project/:projectId/safety/gallery',
  safetyGalleryMap: '/project/:projectId/safety/gallery/map',
  safetyAlerts: '/project/:projectId/safety/alerts',
  progressOverview: '/project/:projectId/progress',
  progressOverviewGantt: '/project/:projectId/progress/gantt',
  progressOverviewCharts: '/project/:projectId/progress/charts',
  progressOverviewReports: '/project/:projectId/progress/reports',
  progressOverviewAlerts: '/project/:projectId/progress/alerts',
  progressFloor: '/project/:projectId/progress/floor/:floorId', // use ?tracker=<tracker>
  progressFloorPoint:
    '/project/:projectId/progress/floor/:floorId/point/:pointId',
  progressFloorMulti3d: '/project/:projectId/progress/floor/:floorId/multi-3d', // use ?trackers=a,b&date=
  reportsOverview: '/project/:projectId/reports',
  reportsNew: '/project/:projectId/reports/new',
  reportsNewSafety: '/project/:projectId/reports/new/safety',
  reportsNewProgress: '/project/:projectId/reports/new/progress',
  inspectionOverview: '/project/:projectId/inspection',
  inspectionGallery: '/project/:projectId/inspection/gallery',
  qualityOverview: '/project/:projectId/quality',
  adminZone: '/admin',
  adminProject: '/admin/project/:projectId',
  adminFloor: '/admin/project/:projectId/floor/:floorId',
  adminManagePoints: '/admin/project/:projectId/managepoints',
  adminManagePointsFloor:
    '/admin/project/:projectId/floor/:floorId/managepoints',
  adminManageNoGoZones: '/admin/project/:projectId/managenogozones',
  adminManageNoGoZonesFloor:
    '/admin/project/:projectId/floor/:floorId/managenogozones',
  adminManageZones: '/admin/project/:projectId/managezones',
  adminManageZonesFloor: '/admin/project/:projectId/floor/:floorId/managezones',
  adminManageTargetZones: '/admin/project/:projectId/managetargetzones',
  adminManageTargetZonesFloor:
    '/admin/project/:projectId/floor/:floorId/managetargetzones',
  adminSchedules:
    '/admin/project/:projectId/floor/:floorId/schedule/:scheduleId/manageschedules',
  adminManageEquipment: '/admin/project/:projectId/manageequipment',
  adminManageEquipmentFloor:
    '/admin/project/:projectId/floor/:floorId/manageequipment',
  adminManageUsers: '/admin/project/:projectId/manageusers',
  adminManageUsersUser: '/admin/project/:projectId/manageusers/:userId',
  adminScanHealth: '/admin/project/:projectId/scanhealth',
  adminManageReview: '/admin/project/:projectId/review',
  adminManageReviewAi:
    '/admin/project/:projectId/review/task/:taskId/ai',
  reviewOverview: '/project/:projectId/review',
  reviewTask: '/project/:projectId/review/task/:taskId',
  reviewTaskFloor: '/project/:projectId/review/task/:taskId/floor/:floorId',
  reviewTaskPoint:
    '/project/:projectId/review/task/:taskId/floor/:floorId/point/:pointId',

  siteWalk: '/site-walk',
  siteWalkVideoUpload: '/site-walk/upload',
  siteWalkConfirmation: '/site-walk/:siteWalkId/confirm',
  siteWalkConfirmPoint: '/site-walk/:siteWalkId/confirm/:pointId',

  userProfile: '/profile',
  userProfilePassword: '/profile/password',
  userProfileNotificationPreferences: '/profile/notificationpreferences',
  userProfileIntegrations: '/profile/integrations',
  userProfileScheduleImport: '/profile/integrations/schedule-import',
  userProfileTools: '/profile/tools',
}

/**
 * Generates path strings following a defined pattern with passed in args
 */
export const useGeneratedPaths = () => {
  const generateProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.project, {
      projectId: projectId,
    })

  const generateFloorPath = (projectId: string, floorId: string) =>
    generatePath(PATH_STRINGS.floor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generatePointPath = (
    projectId: string,
    floorId: string,
    pointId: string,
  ) =>
    generatePath(PATH_STRINGS.point, {
      projectId: projectId,
      floorId: floorId,
      pointId: pointId,
    })

  const generatePointFullPath = (
    projectId: string,
    floorId: string,
    pointId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper params type
    _params?: any,
  ) =>
    generatePath(PATH_STRINGS.full, {
      projectId: projectId,
      floorId: floorId,
      pointId: pointId,
    })

  const generateProjectSafetyPath = (projectId: string) =>
    generatePath(PATH_STRINGS.safetyOverview, {
      projectId: projectId,
    })

  const generateProjectSafetyMetricDetailPath = (
    projectId: string,
    categorySlug?: string,
  ) => {
    let path = generatePath(PATH_STRINGS.safetyMetricDetail, {
      projectId: projectId,
    })

    if (categorySlug) {
      path += `?category=${categorySlug}`
    }

    return path
  }

  const generateProjectSafetyMetricDetailFloorPath = (
    projectId: string,
    floorId: string,
    categorySlug?: string,
  ) => {
    let path = generatePath(PATH_STRINGS.safetyMetricDetailFloor, {
      projectId: projectId,
      floorId: floorId,
    })

    if (categorySlug) {
      path += `?category=${categorySlug}`
    }

    return path
  }

  const generateProjectSafetyMetricDetailFloorPointPath = (
    projectId: string,
    floorId: string,
    pointId: string,
  ) => {
    return generatePath(PATH_STRINGS.safetyMetricDetailFloorPoint, {
      projectId: projectId,
      floorId: floorId,
      pointId: pointId,
    })
  }

  const generateSafetyGalleryPath = (projectId: string, floorCode?: string) => {
    const base = generatePath(PATH_STRINGS.safetyGallery, {
      projectId: projectId,
    })
    if (floorCode) {
      return `${base}?floor=${encodeURIComponent(floorCode)}`
    }
    return base
  }

  const generateSafetyGalleryMapPath = (
    projectId: string,
    floorCode?: string,
  ) => {
    const base = generatePath(PATH_STRINGS.safetyGalleryMap, {
      projectId: projectId,
    })
    if (floorCode) {
      return `${base}?floor=${encodeURIComponent(floorCode)}`
    }
    return base
  }

  const generateSafetyAlertsPath = (projectId: string) =>
    generatePath(PATH_STRINGS.safetyAlerts, {
      projectId: projectId,
    })

  const generateProjectProgressPath = (projectId: string) =>
    generatePath(PATH_STRINGS.progressOverview, {
      projectId: projectId,
    })

  const generateProjectReportsPath = (projectId: string) =>
    generatePath(PATH_STRINGS.reportsOverview, {
      projectId: projectId,
    })

  const generateProjectReportsNewPath = (projectId: string) =>
    generatePath(PATH_STRINGS.reportsNew, {
      projectId: projectId,
    })

  const generateProjectReportsNewSafetyPath = (projectId: string) =>
    generatePath(PATH_STRINGS.reportsNewSafety, {
      projectId: projectId,
    })

  const generateProjectReportsNewProgressPath = (projectId: string) =>
    generatePath(PATH_STRINGS.reportsNewProgress, {
      projectId: projectId,
    })

  const generateProjectInspectionPath = (projectId: string) =>
    generatePath(PATH_STRINGS.inspectionOverview, {
      projectId: projectId,
    })

  const generateInspectionGalleryPath = (projectId: string) =>
    generatePath(PATH_STRINGS.inspectionGallery, {
      projectId: projectId,
    })

  const generateProjectQualityPath = (projectId: string) =>
    generatePath(PATH_STRINGS.qualityOverview, {
      projectId: projectId,
    })

  const generateProjectProgressGanttPath = (projectId: string) =>
    generatePath(PATH_STRINGS.progressOverviewGantt, {
      projectId: projectId,
    })

  const generateProjectProgressChartsPath = (projectId: string) =>
    generatePath(PATH_STRINGS.progressOverviewCharts, {
      projectId: projectId,
    })

  const generateProjectProgressReportsPath = (projectId: string) =>
    generatePath(PATH_STRINGS.progressOverviewReports, {
      projectId: projectId,
    })

  const generateProjectProgressAlertsPath = (projectId: string) =>
    generatePath(PATH_STRINGS.progressOverviewAlerts, {
      projectId: projectId,
    })

  const generateProjectProgressFloorPath = (
    projectId: string,
    floorId: string,
  ) =>
    generatePath(PATH_STRINGS.progressFloor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateProjectProgressFloorPointPath = (
    projectId: string,
    floorId: string,
    pointId: string,
  ) =>
    generatePath(PATH_STRINGS.progressFloorPoint, {
      projectId: projectId,
      floorId: floorId,
      pointId: pointId,
    })

  const generateProjectProgressFloorMulti3dPath = (
    projectId: string,
    floorId: string,
  ) =>
    generatePath(PATH_STRINGS.progressFloorMulti3d, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateManagePointsProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManagePoints, {
      projectId: projectId,
    })

  const generateManagePointsFloorPath = (projectId: string, floorId: string) =>
    generatePath(PATH_STRINGS.adminManagePointsFloor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateManageNoGoZonesProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManageNoGoZones, {
      projectId: projectId,
    })

  const generateManageNoGoZonesFloorPath = (
    projectId: string,
    floorId: string,
  ) =>
    generatePath(PATH_STRINGS.adminManageNoGoZonesFloor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateManageZonesProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManageZones, {
      projectId: projectId,
    })

  const generateManageZonesFloorPath = (projectId: string, floorId: string) =>
    generatePath(PATH_STRINGS.adminManageZonesFloor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateManageTargetZonesProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManageTargetZones, {
      projectId: projectId,
    })

  const generateManageTargetZonesFloorPath = (
    projectId: string,
    floorId: string,
  ) =>
    generatePath(PATH_STRINGS.adminManageTargetZonesFloor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateAdminProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminProject, {
      projectId: projectId,
    })

  const generateManageSchedulesPath = (
    projectId: string,
    floorId: string,
    scheduleId: string,
  ) =>
    generatePath(PATH_STRINGS.adminSchedules, {
      projectId: projectId,
      floorId: floorId,
      scheduleId: scheduleId,
    })

  const generateManageEquipmentProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManageEquipment, {
      projectId: projectId,
    })

  const generateManageEquipmentFloorPath = (
    projectId: string,
    floorId: string,
  ) =>
    generatePath(PATH_STRINGS.adminManageEquipmentFloor, {
      projectId: projectId,
      floorId: floorId,
    })

  const generateManageUsersProjectPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManageUsers, {
      projectId: projectId,
    })

  const generateManageUsersProjectUserPath = (
    projectId: string,
    userId: string,
  ) =>
    generatePath(PATH_STRINGS.adminManageUsersUser, {
      projectId,
      userId,
    })

  const generateAdminScanHealthPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminScanHealth, {
      projectId,
    })

  const generateAdminManageReviewPath = (projectId: string) =>
    generatePath(PATH_STRINGS.adminManageReview, {
      projectId,
    })

  const generateReviewOverviewPath = (projectId: string) =>
    generatePath(PATH_STRINGS.reviewOverview, {
      projectId,
    })

  const generateReviewTaskPath = (projectId: string, taskId: string) =>
    generatePath(PATH_STRINGS.reviewTask, {
      projectId,
      taskId,
    })

  const generateAdminReviewAiPath = (projectId: string, taskId: string) =>
    generatePath(PATH_STRINGS.adminManageReviewAi, {
      projectId,
      taskId,
    })

  const generateReviewTaskFloorPath = (
    projectId: string,
    taskId: string,
    floorId: string,
  ) =>
    generatePath(PATH_STRINGS.reviewTaskFloor, {
      projectId,
      taskId,
      floorId,
    })

  const generateReviewTaskPointPath = (
    projectId: string,
    taskId: string,
    floorId: string,
    pointId: string,
  ) =>
    generatePath(PATH_STRINGS.reviewTaskPoint, {
      projectId,
      taskId,
      floorId,
      pointId,
    })

  const generateFilterBuildingsPath = (
    inAdminZone: boolean,
    mode: string,
    sort: string,
  ) => {
    const path = inAdminZone ? PATH_STRINGS.adminZone : '/'

    return `${path}?mode=${mode}&sort=${sort}`
  }

  const generateSiteWalkConfirmationPath = (siteWalkId: string | number) => {
    return generatePath(PATH_STRINGS.siteWalkConfirmation, {
      siteWalkId: siteWalkId,
    })
  }

  const generateSiteWalkConfirmPointPath = (
    siteWalkId: string | number,
    pointId: string | number,
  ) => {
    return generatePath(PATH_STRINGS.siteWalkConfirmPoint, {
      siteWalkId: siteWalkId,
      pointId: pointId,
    })
  }

  return {
    generateProjectPath,
    generateFloorPath,
    generatePointPath,
    generatePointFullPath,
    generateProjectSafetyPath,
    generateProjectSafetyMetricDetailPath,
    generateProjectSafetyMetricDetailFloorPath,
    generateProjectSafetyMetricDetailFloorPointPath,
    generateSafetyGalleryPath,
    generateSafetyGalleryMapPath,
    generateSafetyAlertsPath,
    generateManagePointsProjectPath,
    generateManagePointsFloorPath,
    generateManageNoGoZonesFloorPath,
    generateManageNoGoZonesProjectPath,
    generateManageZonesProjectPath,
    generateManageZonesFloorPath,
    generateManageTargetZonesProjectPath,
    generateManageTargetZonesFloorPath,
    generateProjectProgressPath,
    generateProjectProgressGanttPath,
    generateProjectProgressChartsPath,
    generateProjectProgressReportsPath,
    generateProjectProgressAlertsPath,
    generateProjectProgressFloorPath,
    generateProjectProgressFloorPointPath,
    generateProjectProgressFloorMulti3dPath,
    generateProjectReportsPath,
    generateProjectReportsNewPath,
    generateProjectReportsNewSafetyPath,
    generateProjectReportsNewProgressPath,
    generateAdminProjectPath,
    generateManageSchedulesPath,
    generateManageEquipmentProjectPath,
    generateManageEquipmentFloorPath,
    generateManageUsersProjectPath,
    generateManageUsersProjectUserPath,
    generateAdminScanHealthPath,
    generateAdminManageReviewPath,
    generateReviewOverviewPath,
    generateReviewTaskPath,
    generateAdminReviewAiPath,
    generateReviewTaskFloorPath,
    generateReviewTaskPointPath,
    generateFilterBuildingsPath,
    generateProjectInspectionPath,
    generateInspectionGalleryPath,
    generateProjectQualityPath,
    generateSiteWalkConfirmationPath,
    generateSiteWalkConfirmPointPath,
  }
}
